import { Form } from '@ant-design/compatible'

// import '@ant-design/compatible/assets/index.css';

import { Card, Checkbox, Input, Modal, Select } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import { invitationUsesValidator } from '../../dux/actions/utils/helpers'
import InvitationAccessFields, {
  approvalFieldDefaults,
  readApprovalFields,
} from './InvitationAccessFields'

const FormItem = Form.Item

class EditInvitationModal extends React.Component {
  state = {
    searchFilter: '',
  }

  onSearchInput = (val) => this.setState(() => ({ searchFilter: val }))

  saveFormRef = (ref) => (this.form = ref)

  onSave = () => {
    this.form.validateFields((err, values) => {
      if (!err) {
        const invite = {
          ...this.props.modalProps.record,
          groups: values.groups,
          uses: values.uses !== undefined ? parseFloat(values.uses) : 0,
          valid: values.valid,
          notifyAdmins: !!values.notifyAdmins,
          ...readApprovalFields(values),
        }

        // Only a code that still carries a limit can be dropped below its
        // own registration count. `uses: 0` means unlimited, and 0 is below
        // every count -- reading it as a limit invalidated any code with
        // registrations the moment someone unchecked "Limit Invitations",
        // which is exactly how you lift a cap.
        const limited = parseFloat(values.uses) > 0
        if (
          limited &&
          values.uses <= this.props.modalProps.record.registrations
        ) {
          return Modal.confirm({
            title: 'Confirm Invalidation',
            content:
              "You've modified the registration limit to be below the current number of registrations. This action will not affect existing users, but will invalidate this invitation code.",
            okText: 'Save Edits Anyway',
            cancelText: 'Go Back',
            onCancel: () => Promise.resolve({}),
            onOk: () => {
              invite.valid = false
              this.props.invitationActions.editInvitation(invite)
            },
          })
        } else {
          this.props.invitationActions.editInvitation(invite)
        }
      }
    })
  }
  render() {
    return (
      <Modal
        title="Edit Invitation"
        okText="Save"
        onOk={this.onSave}
        cancelText="Cancel"
        onCancel={this.props.modalActions.closeModal}
        open={this.props.modal.modalOpen}
        confirmLoading={this.props.appState.asyncPending}
        maskClosable={false}
      >
        <EditInvitationForm
          ref={this.saveFormRef}
          record={this.props.modalProps.record}
          groups={Object.values(this.props.permissions.groups || {})}
          users={this.props.permissions.users}
          onSearchInput={this.onSearchInput}
          searchFilter={this.state.searchFilter}
        />
      </Modal>
    )
  }
}

const EditInvitationForm = Form.create()((props) => {
  const { form } = props
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form
  const acceptedUsers = Object.keys(props.users).filter(
    (user) =>
      props.users[user].invitation === props.record.id &&
      props.users[user].email.includes(props.searchFilter),
  )

  const [limitRegistrations, setLimitRegistrations] = React.useState(
    props.record.uses > 0,
  )

  // An invitation can name a group that has since been deleted (registration
  // skips it, so the invitation still works, but nobody can see it in the
  // DAM). Surface those as options so they show up in the picker and can be
  // removed, rather than silently riding along on every save.
  const knownGroups = (props.groups || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
  const knownIds = new Set(knownGroups.map((g) => g.id))
  const orphanOptions = (props.record.groups || [])
    .filter((id) => !knownIds.has(id))
    .map((id) => ({ id, name: `${id} (deleted group)` }))
  const groupOptions = knownGroups.concat(orphanOptions)

  const handleLimitCheckboxChange = (e) => {
    const checked = e.target.checked
    setLimitRegistrations(checked)

    if (!checked) {
      // When unchecked, set the registration limit to 0
      setFieldsValue({ uses: 0 })
    }
  }

  return (
    <Form>
      <div className=".modal__form-item-flex-container">
        <FormItem label="Invitation Code" require={true}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {getFieldDecorator('id', {
              initialValue: props.record.id,
            })(<Input disabled style={{ width: 'calc(100% - 80px)' }} />)}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Active:</span>
              {getFieldDecorator('valid', {
                valuePropName: 'checked',
                initialValue: props.record.valid,
              })(<Checkbox />)}
            </div>
          </div>
        </FormItem>

        <FormItem
          label="Groups"
          help="Users who register with this invitation are added to these groups. Changing them affects future registrations only; existing users keep their current groups."
        >
          {getFieldDecorator('groups', {
            initialValue: props.record.groups || [],
            rules: [
              {
                required: true,
                message: 'At least one group is required',
              },
            ],
          })(
            <Select
              mode="multiple"
              defaultActiveFirstOption={false}
              placeholder="Add one or more groups"
              filterOption
              optionFilterProp="name"
            >
              {groupOptions.map((group) => (
                <Select.Option
                  key={group.id}
                  value={group.id}
                  name={group.name}
                >
                  {group.name}
                </Select.Option>
              ))}
            </Select>,
          )}
        </FormItem>

        <FormItem label="Limit Invitations">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: '40px',
            }}
          >
            <Checkbox
              checked={limitRegistrations}
              onChange={handleLimitCheckboxChange}
            />
            {limitRegistrations && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span>Max Uses:</span>
                {getFieldDecorator('uses', {
                  validateTrigger: 'onBlur',
                  initialValue: props.record.uses,
                  rules: [
                    {
                      validator: invitationUsesValidator,
                    },
                  ],
                })(<Input type="number" style={{ width: '100px' }} />)}
              </div>
            )}
          </div>
        </FormItem>

        <FormItem
          label="Notify admins on use"
          extra="Email tenant admins each time someone registers with this code and confirms their email"
        >
          {getFieldDecorator('notifyAdmins', {
            valuePropName: 'checked',
            initialValue: !!props.record.notifyAdmins,
          })(<Checkbox />)}
        </FormItem>

        <InvitationAccessFields
          FormItem={FormItem}
          getFieldDecorator={getFieldDecorator}
          getFieldValue={getFieldValue}
          groups={groupOptions}
          defaults={{
            adminOnly: !!props.record.adminOnly,
            ...approvalFieldDefaults(props.record),
          }}
        />

        <Card
          title="Accepted Users"
          extra={
            <Input.Search
              placeholder="email..."
              onChange={(e) => props.onSearchInput(e.target.value)}
              style={{ width: 200 }}
            />
          }
        >
          <div className="modal__scrollable-list">
            {acceptedUsers.map((user) => (
              <div key={props.users[user].id}>{props.users[user].email}</div>
            ))}
          </div>
        </Card>
      </div>
    </Form>
  )
})
/*
 *  Props are from the redux store
 *  "actions" are objects created by bindActionCreators, containing all relevant action functions
 *  modalProps is object passed from modal creator
 */
EditInvitationModal.propTypes = {
  modal: PropTypes.object,
  modalActions: PropTypes.object,
  modalProps: PropTypes.object,
  appState: PropTypes.shape({
    asyncPending: PropTypes.bool,
  }),
  onSubmit: PropTypes.func,
}

EditInvitationModal.defaultProps = {
  modalProps: {},
}

export default EditInvitationModal
